import useSession from 'components/auth/useSession'
import { usePecField } from 'components/form/final-form/hooks/useField'
import { PecSwitch } from 'components/route'
import React, { useMemo } from 'react'
import { match, Redirect, Route } from 'react-router'
import { emptyArray } from 'util/array'
import { grupoCboMedicoes } from 'view/atendimentos/detail/soap/objetivo/acessos'
import { TipoPreNatal } from 'view/atendimentos/detail/soap/pre-natal/model-preNatal'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'

import {
  convertMedicaoAnterior,
  convertMedicaoAnteriorRetificacao,
  convertMedicaoAtendimentoAtual,
  convertMedicaoOutrosAtendimentos,
  convertMedicoesNoNascimento,
} from './converter-medicoes'
import { HistoricoMedicoesAntropometriaView } from './HistoricoMedicoesAntropometriaView'
import { HistoricoMedicoesGlicemiaView } from './HistoricoMedicoesGlicemiaView'
import { EditableHistoricoMedicoesModalProps } from './HistoricoMedicoesModal'
import { HistoricoMedicoesSinaisVitaisView } from './HistoricoMedicoesSinaisVitaisView'
import { MedicaoAnteriorFormModel } from './MedicaoAnteriorForm'
import { MedicoesAtendimentoAnterior, MedicoesNascimento } from './model-medicoes'
import { HistoricoMedicoesPreNatalView } from './pre-natal/HistoricoMedicoesPreNatalView'
import { getMedicoesAntropometricasPreNatal } from './pre-natal/util-medicoesPreNatal'

export interface EditableHistoricoMedicoesModalBodyProps extends EditableHistoricoMedicoesModalProps {
  initialRoute: string
  match: match
  medicoes: ReadonlyArray<MedicoesAtendimentoAnterior>
  medicoesNoNascimento: MedicoesNascimento
  isAtendimentoObservacao: boolean
  tipoPreNatal?: TipoPreNatal
  isRetificacao: boolean
}

export function EditableHistoricoMedicoesModalBody({
  fieldName,
  prontuarioId,
  cidadao,
  dataAtendimento,
  initialRoute,
  match,
  medicoes = emptyArray,
  medicoesAtendimentoAtual,
  medicoesNoNascimento,
  preNatalAtendimentoAtual,
  dumAtendimentoAtual,
  resultadosExamesAtendimentoAtual,
  gestacoes,
  isAtendimentoObservacao,
  tipoPreNatal,
  isRetificacao,
}: EditableHistoricoMedicoesModalBodyProps) {
  const formField = usePecField({ name: fieldName })
  const { checkAuthorization } = useSession()
  const medicoesAnteriores: MedicaoAnteriorFormModel[] = formField.input.value ?? []

  const medicoesExibir: HistoricoMedicaoModel[] = useMemo(() => {
    if (!isRetificacao) {
      return [
        ...medicoesAnteriores.map(convertMedicaoAnterior),
        ...(medicoesAtendimentoAtual
          ? [convertMedicaoAtendimentoAtual(medicoesAtendimentoAtual, dataAtendimento)]
          : []),
        ...[...medicoes].sort((a, b) => b.dataMedicao - a.dataMedicao).map(convertMedicaoOutrosAtendimentos),
        ...(medicoesNoNascimento ? [convertMedicoesNoNascimento(medicoesNoNascimento, cidadao.dataNascimento)] : []),
      ]
    } else {
      return [
        ...medicoesAnteriores.map(convertMedicaoAnteriorRetificacao).sort((a, b) => b.dataMedicao - a.dataMedicao),
      ]
    }
  }, [
    cidadao.dataNascimento,
    dataAtendimento,
    isRetificacao,
    medicoes,
    medicoesAnteriores,
    medicoesAtendimentoAtual,
    medicoesNoNascimento,
  ])

  const onAddMedicaoAnterior = (medicao: MedicaoAnteriorFormModel) => {
    formField.input.onChange([medicao, ...formField.input.value])
    formField.input.onBlur()
  }

  const onRemoveMedicaoAnterior = (id: ID) => {
    formField.input.onChange([...formField.input.value?.filter((m: MedicaoAnteriorFormModel) => m.id !== id)])
    formField.input.onBlur()
  }

  return (
    <PecSwitch>
      <Redirect exact path={match.url} to={`${match.url}${initialRoute}`} />
      <Route
        path={`${match.url}/pre-natal`}
        render={(renderProps) => (
          <HistoricoMedicoesPreNatalView
            {...renderProps}
            prontuarioId={prontuarioId}
            preNatalAtendimentoAtual={preNatalAtendimentoAtual}
            resultadosExamesAtendimentoAtual={resultadosExamesAtendimentoAtual}
            dumAtendimentoAtual={dumAtendimentoAtual}
            dataAtendimento={dataAtendimento}
            medicoesAntropometricasPreNatal={getMedicoesAntropometricasPreNatal(medicoesExibir, tipoPreNatal)}
            tipoPreNatal={tipoPreNatal}
          />
        )}
      />
      <Route
        path={`${match.url}/antropometria`}
        render={(renderProps) => (
          <HistoricoMedicoesAntropometriaView
            {...renderProps}
            canEditOrAdd={checkAuthorization(grupoCboMedicoes.adicionar)}
            medicoes={medicoesExibir}
            cidadao={cidadao}
            gestacoes={gestacoes}
            dataAtendimento={dataAtendimento}
            isAtendimentoObservacao={isAtendimentoObservacao}
            onAddMedicaoAnterior={onAddMedicaoAnterior}
            onRemoveMedicaoAnterior={onRemoveMedicaoAnterior}
          />
        )}
      />
      <Route
        path={`${match.url}/sinaisVitais`}
        render={(renderProps) => (
          <HistoricoMedicoesSinaisVitaisView {...renderProps} medicoes={medicoesExibir} gestacoes={gestacoes} />
        )}
      />
      <Route
        path={`${match.url}/glicemia`}
        render={(renderProps) => (
          <HistoricoMedicoesGlicemiaView {...renderProps} medicoes={medicoesExibir} gestacoes={gestacoes} />
        )}
      />
    </PecSwitch>
  )
}
